<!-- ? V2 1XP -->
<script lang="ts">
import { isMobile } from '../../utils/breakpoints';
import Modal from '../Modal/Modal.vue';
import ShopCartLink from '../ShopCartLink/ShopCartLink.vue';
import Loading from '../Loading/Loading.vue';
import { onMounted, ref, watch } from 'vue';
import IndividualPrice from '../IndividualPrice/IndividualPrice.vue';
import { convertISODateTimeToISODate, convertISODateTimeToLocaleDate } from '../../utils/dateTime';
import Icon from '../Icon/Icon.vue';
import Textfield1XP from '../Textfield1XP/Textfield1XP.vue';
import Button1XP from '../Button1XP/Button1XP.vue';
import type { WindowWika } from '../../types/window';
import { useStoreConfiguration } from '../../store/configuration/configuration';
import { useStoreTranslations } from '../../store/translations/translations.ts';
import Divider from '../Divider/Divider.vue';
import { post } from '../../xhr/post';
import withBaseUrl from '../../xhr/withBaseUrl';
import FormNumber from '../FormNumber/FormNumber.vue';
import Notifications from '../Notifications/Notifications.vue';

const _window = window as unknown as WindowWika;
</script>

<script setup lang="ts">
const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
});

const componentKey = ref<number>(0);
const isModalOpen = ref(false);
const showNotification = ref<boolean>(false);
const searchSuccess = ref(false);
const isLoading = ref(false);
const articleNumber = ref('');
const buildCode = ref('');
const quantity = ref('1');
const data = ref<{
  Longtext?: string;
  Shorttext?: string;
  ProductType?: string;
  DeliveryCategory?: number;
  DeliveryCategoryValue?: number;
}>({});
const price = ref('');
const deliveryDays = ref('');
const deliveryCategory = ref('');
const workDays = ref('');
const warningText = ref('');
const addButton = ref(null);
const bType = ref('');
const codestring = ref('');
const shopCartLink = ref<{ addItem: () => void } | null>(null);
const workingDate = ref('');
const amountWarningText = ref('');
const isAmountWarningOpen = ref(false);
const storeConfiguration = useStoreConfiguration();
const configurations = ref(storeConfiguration.config);
const storeTranslations = useStoreTranslations();
const translations = storeTranslations.translations;
const currentCountry = ref<string | undefined>(
  storeConfiguration.config?.caas_restServiceShopCountry,
);
const abort = ref<boolean | undefined>(undefined);

const getDeliveryDate = async () => {
  const postData = {
    fromdate: convertISODateTimeToISODate(new Date().toISOString()),
    'ax-system': props.config.deliveryAxSystem,
    days: deliveryDays.value,
  };

  try {
    if (!configurations.value) {
      return;
    }

    const responseData = await post<{ result?: Array<{ workingDate?: string }> }>(
      withBaseUrl({
        config: configurations.value,
        url: configurations.value.caas_deliveryDateUrl,
      }),
      postData,
    );

    if (responseData?.result?.[0].workingDate && currentCountry.value) {
      workingDate.value = convertISODateTimeToLocaleDate(
        responseData.result[0].workingDate,
        currentCountry.value,
        'No delivery date available',
      );
    } else {
      throw new Error();
    }
  } catch (error) {
    console.error(error);
  }
};

const addItem = (event) => {
  addButton.value = event.target.value;
  addButton.value && shopCartLink.value?.addItem();
};

const closeModal = () => {
  isModalOpen.value = false;
};

const updateQuantity = (newQuantity) => {
  quantity.value = newQuantity.toString();
};

const checkAvailability = () => {
  showNotification.value = false;
  abort.value = false;
  isLoading.value = true;
  const itemid = typeof articleNumber.value !== 'undefined' ? articleNumber.value : '';
  const buildid = typeof buildCode.value !== 'undefined' ? buildCode.value : '';
  const output = typeof buildCode.value !== 'undefined' && buildCode.value != '' ? 'false' : 'true';

  if (!configurations.value) {
    return;
  }

  if (
    (buildid || itemid) &&
    currentCountry.value &&
    (_window.currentLanguage || storeConfiguration.caasConfig?.language)
  ) {
    try {
      post<unknown>(
        withBaseUrl({
          config: configurations.value,
          url: configurations.value?.caas_getItemInfoUrl,
        }),
        {
          itemid,
          buildid,
          output,
          container: configurations.value.pt_search_container,
          shopContainer: configurations.value.pt_search_shop_container,
          country: currentCountry.value,
          language: (_window.currentLanguage || storeConfiguration.caasConfig?.language)?.replace(
            'EN-US',
            'EN',
          ),
        },
      )
        .then((response) => {
          if (!response) {
            warningText.value =
              translations?.value['general.service.warning'] || 'Service currently unavailable';
            searchSuccess.value = false;
            showNotification.value = true;
            isLoading.value = false;
            return;
          }
          displaySuccessModal(response);
        })
        .catch((error: Error) => {
          isLoading.value = false;
          warningText.value =
            translations?.value['general.service.warning'] ||
            error ||
            'Service currently unavailable';
          searchSuccess.value = false;
          showNotification.value = true;
          isLoading.value = false;
        });
    } catch (error) {
      console.error(error);
    }
  } else {
    warningText.value = props.config.warningNoValueSubmitted;
    searchSuccess.value = false;
    showNotification.value = true;
    isLoading.value = false;
  }
};

const handleAmountWarning = (msg) => {
  amountWarningText.value = msg;
  isAmountWarningOpen.value = false;
  setTimeout(() => {
    isAmountWarningOpen.value = true;
  }, 300);
};

const handleShopCartLinkError = (newData) => {
  const { isArticleType, url } = newData;

  if (isArticleType && url) {
    showArticleTypeUrl(url);
  }
};

const showArticleTypeUrl = (url) => {
  warningText.value = props.config.warningIsArticleType.replace(
    /{(\w+( +\w+)*)}/,
    `<a href="${url}">$1</a>`,
  );
  isModalOpen.value = true;
};

const displaySuccessModal = (newData) => {
  if (newData.Success) {
    data.value = newData;
    price.value = newData.PositionPrice;
    deliveryDays.value = (
      Number.parseInt(newData.DeliveryTime) + Number.parseInt(props.config.externalTransport)
    ).toString();

    if (newData.DeliveryCategory === 1) {
      deliveryCategory.value = props.config.readyForShipping;
    } else if (newData.DeliveryCategory > 1 && newData.DeliveryCategory < 6) {
      deliveryCategory.value = props.config.deliveryAfterProduction;
    }

    workDays.value =
      newData.DeliveryTime === 0 ? props.config.singularWorkDayStr : props.config.pluralWorkDayStr;

    searchSuccess.value = true;
    isModalOpen.value = true;
  } else if (newData.IsArticleType && newData.Url) {
    searchSuccess.value = false;

    showArticleTypeUrl(newData.Url);
  } else {
    warningText.value = props.config.warningInvalidArticle;
    searchSuccess.value = false;
    showNotification.value = true;
  }

  isLoading.value = false;
  bType.value = newData.MainType;
  codestring.value = newData.CodeString;
};

const checkUrlForItemId = () => {
  const query = _window.location.search.substring(1);
  const params = new URLSearchParams(query);
  const itemId = params.get('itemid');
  const itemIdUpper = params.get('itemId');

  const buildId = params.get('buildid');
  const buildIdUpper = params.get('buildId');

  if (buildId || buildIdUpper) {
    buildCode.value = buildId != null ? buildId : (buildIdUpper ?? '');
    checkAvailability();
  }

  if (itemId || itemIdUpper) {
    articleNumber.value = itemId != null ? itemId : (itemIdUpper ?? '');
    checkAvailability();
  }
};

watch(deliveryDays, (newValue) => {
  if (newValue) {
    getDeliveryDate();
  }
});

watch(
  () => storeConfiguration,
  (newValue) => {
    if (newValue) {
      configurations.value = newValue.config;

      if (newValue.config) {
        currentCountry.value = newValue.config.caas_restServiceShopCountry;
      }
    }
  },
  { immediate: true, deep: true },
);

watch(
  configurations,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      checkUrlForItemId();
      currentCountry.value = newValue.caas_restServiceShopCountry;
    }
  },
  { immediate: true },
);

watch(
  abort,
  (newValue) => {
    if (newValue) {
      showNotification.value = false;
    }
  },
  { immediate: true },
);

watch(
  articleNumber,
  (newValue) => {
    if (newValue) {
      abort.value = false;
    }
  },
  { immediate: true },
);

onMounted(() => {
  checkUrlForItemId();
});
</script>

<template>
  <div :class="$style.container">
    <div :class="$style.upper">
      <div v-if="config.boxTitle" :class="$style.textContainer">
        <Icon name="shopping_cart" size="xLarge" />
        <span :class="$style.title">{{ config.boxTitle }}</span>
        <tippy
          v-tippy="{
            arrow: true,
            distance: 7,
            interactive: true,
            theme: '1xp light tooltip',
            trigger: isMobile ? 'click' : 'mouseenter focus',
            zIndex: 8000000,
          }"
        >
          <Icon :class="$style.info" name="info" size="large" />
          <template #content>
            <div :class="$style.infoContent">
              <div v-html="config.infoPopperContent" />
              <div :class="$style.infoImage">
                <img v-if="config.infoPopperImage" alt="" :src="config.infoPopperImage" />
              </div>
            </div>
          </template>
        </tippy>
      </div>

      <div v-if="config.boxText" :class="$style.text">
        {{ config.boxText }}
      </div>
    </div>

    <form :class="$style.userInput" @submit.prevent>
      <Textfield1XP
        close-label=""
        :is-cancelable="true"
        label="article number"
        :placeholder="config.inputPlaceholder"
        :value="articleNumber"
        @clear="
          (e) => {
            abort = e;
          }
        "
        @input="
          (event) => {
            articleNumber = event.currentTarget.value ?? '';
            isModalOpen = false;
          }
        "
        @keyup.enter="addItem($event)"
      />
      <FormNumber
        :class="$style.quantityInput"
        :initial-value="Number(quantity)"
        label="article quantity"
        @on-change="(quantity) => updateQuantity(quantity)"
      />
    </form>

    <Notifications
      :message="`${config.warningHeadline}: ${warningText}`"
      :open="showNotification && !searchSuccess"
      pattern="inline"
      :type="'warning'"
    />

    <div :class="$style.lower">
      <Button1XP
        :is-disabled="articleNumber.length === 0"
        start-icon="info"
        :text="config.infoButtonText"
        variant="secondary"
        @click="((isModalOpen = false), checkAvailability())"
      />

      <ShopCartLink
        :key="`cartLink1-${componentKey}`"
        ref="shopCartLink"
        :add-button-loading-text="config.addButtonLoadingText"
        :add-item-amount-msg-txt="config.addItemAmountMsgTxt"
        :add-item-failed-msg="config.addArticleFailedMessage"
        :add-item-highest-amount-msg="config.addItemHighestAmountMessage"
        :add-item-lowest-amount-msg="config.addItemLowestAmountMessage"
        :add-item-multiple-amount-msg="config.addItemMultipleAmountMessage"
        :clear-notifications="abort ?? false"
        :disable-amount-snackbar="true"
        :is-dark-background="true"
        :is-disabled="articleNumber.length === 0"
        :item="{
          text: config.addButtonText,
          quantity: quantity,
          id: buildCode && buildCode.trim() !== '' ? buildCode : articleNumber,
          btype: bType,
          codestring: codestring,
          headline: config.headline,
          subheadline: config.subheadline,
          pictureurl: config.pictureurl,
          thumbnailurl: config.thumbnailurl,
          producturl: config.producturl,
        }"
        source="Quick Order"
        @amount-warning="handleAmountWarning"
        @click="
          isModalOpen = false;
          showNotification = false;
        "
        @error="handleShopCartLinkError"
        @is-updating="(e) => (isLoading = e)"
      />

      <div v-if="isLoading" :class="$style.loading">
        <Loading :class="$style.spinner" color="#fff" :size="20" />
        {{ config.loadingText }}
      </div>
    </div>

    <Modal
      :container-class="$style.modal"
      :is-modal-open="isModalOpen && searchSuccess"
      @close-modal="closeModal"
    >
      <div :class="$style.modalInner">
        <p :class="$style.heading">{{ config.inputPlaceholder }}: {{ articleNumber }}</p>
        <div class="columns is-gapless">
          <div class="column is-two-thirds-tablet">
            <p :class="$style.subHeading">
              {{ config.articleDetails }}
            </p>
            <p
              v-if="data.Longtext"
              v-html="`${data.Longtext.replace(/(?:\r\n|\r|\n)/g, '<br>')}`"
            />
            <p v-else v-html="`${data.Shorttext?.replace(/(?:\r\n|\r|\n)/g, '<br>')}`" />
          </div>
          <div class="column is-one-third-tablet">
            <div :class="$style.addToCart">
              <div :class="[$style.singlePrice, 'clearfix']">
                <span :class="$style.label"> {{ config.pricePerItem }}: </span>
                <span :class="$style.label">
                  <IndividualPrice
                    :btype-list="
                      data
                        ? [
                            {
                              btype: data.ProductType,
                              minimalPrice: price,
                            },
                          ]
                        : []
                    "
                    :product-type="data?.ProductType"
                    :value="Number(price)"
                  />
                </span>
              </div>
              <div :class="$style.quantity">
                <span :class="$style.label">{{ config.quantity }}:</span>
                <div :class="$style.quantitySelect">
                  <FormNumber
                    :initial-value="Number(quantity)"
                    is-white
                    label="item quantity"
                    @on-change="(quantity) => updateQuantity(quantity)"
                  />
                </div>
              </div>
              <Divider :class="$style.divider" />
              <div :class="$style.sum">
                <IndividualPrice
                  :btype-list="
                    data
                      ? [
                          {
                            btype: data.ProductType,
                            minimalPrice: price,
                          },
                        ]
                      : []
                  "
                  :product-type="data?.ProductType"
                  :quantity="Number(quantity)"
                  :value="Number(price)"
                />
              </div>
              <div :class="$style.tax">
                {{ config.tax }} &amp;
                <a :href="config.shippingUrl">
                  {{ config.shippingText }}
                </a>
              </div>
              <div
                :class="[
                  {
                    QuickAddBox__deliveryStatusGreen: data.DeliveryCategory === 1,
                  },
                  {
                    QuickAddBox__deliveryStatusOrange:
                      data.DeliveryCategory === 2 || data.DeliveryCategoryValue === 3,
                  },
                ]"
              >
                <span :class="$style.deliveryStatus" />
                {{ deliveryCategory }}
                <span v-if="data.DeliveryCategory !== 1" :class="$style.deliveryDate">
                  {{ config.shippingText }}:
                  {{ workingDate }}
                </span>
              </div>
              <div :class="[$style.buttons, { QuickAddBox__buttonsHidden: isLoading }]">
                <ShopCartLink
                  :key="`cartLink2-${componentKey}`"
                  ref="addItemEvent"
                  :add-button-loading-text="config.addButtonLoadingText"
                  :add-item-failed-msg="config.addArticleFailedMessage"
                  :add-item-highest-amount-msg="config.addItemHighestAmountMessage"
                  :add-item-lowest-amount-msg="config.addItemLowestAmountMessage"
                  :add-item-multiple-amount-msg="config.addItemMultipleAmountMessage"
                  :clear-notifications="abort ?? false"
                  :disable-amount-snackbar="true"
                  :is-dark-background="true"
                  :item="{
                    text: config.addButtonText,
                    quantity: quantity,
                    id: buildCode && buildCode.trim() !== '' ? buildCode : articleNumber,
                    headline: config.headline,
                    subheadline: config.subheadline,
                    pictureurl: config.pictureurl,
                    thumbnailurl: config.thumbnailurl,
                    producturl: config.producturl,
                  }"
                  source="Quick Order"
                  @amount-warning="handleAmountWarning"
                  @closed="closeModal"
                />
                <div v-if="isLoading" :class="$style.loading">
                  <Loading :class="$style.spinner" color="#fff" :size="30" />
                  {{ config.addButtonLoadingText }}
                </div>
                <Notifications
                  :message="`${config.warningHeadline}: ${warningText}`"
                  :open="showNotification && !searchSuccess"
                  pattern="inline"
                  :type="'warning'"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
    <Notifications
      :message="amountWarningText"
      :open="isAmountWarningOpen"
      pattern="toast"
      type="info"
      @update:open="isAmountWarningOpen = $event"
    />
  </div>
</template>

<style module lang="scss">
@forward './QuickAddBox.scss';
</style>
